import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ImageIcon,
  Loader2,
  PackageCheck,
  Phone,
  ReceiptText,
  ShoppingBag,
  Timer,
  Truck,
  UserRound,
  WalletCards,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ordersApi } from '../../api';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/Orders/order-details.css';

const statusIcons = {
  pending: Clock3,
  accepted: CheckCircle2,
  preparing: Timer,
  ready: PackageCheck,
  delivered: Truck,
  cancelled: XCircle,
};

const MANAGER_ROLES = [
  'restaurantmanager',
  'restaurantmanagerrole',
  'manager',
  'admin',
  'administrator',
  'superadmin',
  'superadministrator',
];

const WAITER_ROLES = [
  'waiter',
  'serveur',
];

const KITCHEN_ROLES = [
  'cuisine',
  'kitchen',
  'cuisiner',
  'cuisinier',
];

const WAITER_STATUS_ACTIONS = {
  pending: [
    'accepted',
    'cancelled',
  ],
  ready: [
    'delivered',
    'cancelled',
  ],
};

const KITCHEN_STATUS_ACTIONS = {
  accepted: [
    'preparing',
    'cancelled',
  ],
  preparing: [
    'ready',
    'cancelled',
  ],
};

const WAITER_VISIBLE_STATUSES = [
  'pending',
  'ready',
];

const KITCHEN_VISIBLE_STATUSES = [
  'accepted',
  'preparing',
];

const normalizeRole = (role) => {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
};

const normalizeStatus = (status) => {
  return String(status || 'pending')
    .trim()
    .toLowerCase();
};

const isManagerRole = (role) => {
  return MANAGER_ROLES.includes(
    normalizeRole(role),
  );
};

const isWaiterRole = (role) => {
  return WAITER_ROLES.includes(
    normalizeRole(role),
  );
};

const isKitchenRole = (role) => {
  return KITCHEN_ROLES.includes(
    normalizeRole(role),
  );
};

const toArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (
    value &&
    typeof value === 'object'
  ) {
    return Object.values(value);
  }

  return [];
};

const getOrderItemImage = (item) => {
  const product = item?.product || {};
  const images = toArray(
    product?.images,
  );

  const primaryImage =
    product?.primary_image ||
    images.find((image) =>
      Boolean(image?.is_primary),
    ) ||
    images[0];

  return resolveMediaUrl(
    item?.product_snapshot?.image ||
      item?.product_image ||
      item?.image_url ||
      primaryImage?.url ||
      primaryImage?.path ||
      product?.image_url ||
      product?.image,
  );
};

function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const {
    t,
    language,
  } = useLanguage();

  const { showAlert } = useAlerts();
  const { user } = useAuth();

  const [order, setOrder] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState('');

  const [
    updatingStatus,
    setUpdatingStatus,
  ] = useState(null);

  const currentRole = user?.role;

  const managerRole =
    isManagerRole(currentRole);

  const waiterRole =
    isWaiterRole(currentRole);

  const kitchenRole =
    isKitchenRole(currentRole);

  const statusOptions = useMemo(
    () => [
      {
        value: 'pending',
        label: t(
          'orders.status.pending',
        ),
      },
      {
        value: 'accepted',
        label: t(
          'orders.status.accepted',
        ),
      },
      {
        value: 'preparing',
        label: t(
          'orders.status.preparing',
        ),
      },
      {
        value: 'ready',
        label: t(
          'orders.status.ready',
        ),
      },
      {
        value: 'delivered',
        label: t(
          'orders.status.delivered',
        ),
      },
      {
        value: 'cancelled',
        label: t(
          'orders.status.cancelled',
        ),
      },
    ],
    [t],
  );

  const orderStatus =
    normalizeStatus(
      order?.status,
    );

  const groupedOrderItems =
    useMemo(() => {
      const items =
        Array.isArray(order?.items)
          ? order.items
          : [];

      const grouped = new Map();

      items.forEach((item) => {
        const key = [
          item.product_id ||
            item.product_name ||
            '',
          item.product_size_id ||
            item.size_name ||
            '',
          item.unit_price || '',
          item.note || '',
        ].join('|');

        if (!grouped.has(key)) {
          grouped.set(key, {
            ...item,
            quantity: Number(
              item.quantity || 0,
            ),
            total_price: Number(
              item.total_price || 0,
            ),
          });

          return;
        }

        const currentItem =
          grouped.get(key);

        grouped.set(key, {
          ...currentItem,
          quantity:
            Number(
              currentItem.quantity ||
                0,
            ) +
            Number(
              item.quantity || 0,
            ),
          total_price:
            Number(
              currentItem.total_price ||
                0,
            ) +
            Number(
              item.total_price || 0,
            ),
        });
      });

      return Array.from(
        grouped.values(),
      );
    }, [order?.items]);

  const allowedNextStatuses =
    useMemo(() => {
      if (!order) {
        return [];
      }

      if (managerRole) {
        return statusOptions
          .map(
            (option) =>
              option.value,
          )
          .filter(
            (status) =>
              status !==
              orderStatus,
          );
      }

      if (waiterRole) {
        return (
          WAITER_STATUS_ACTIONS[
            orderStatus
          ] || []
        );
      }

      if (kitchenRole) {
        return (
          KITCHEN_STATUS_ACTIONS[
            orderStatus
          ] || []
        );
      }

      return [];
    }, [
      order,
      managerRole,
      waiterRole,
      kitchenRole,
      orderStatus,
      statusOptions,
    ]);

  const visibleStatusOptions =
    useMemo(() => {
      if (managerRole) {
        return statusOptions;
      }

      return statusOptions.filter(
        (option) =>
          allowedNextStatuses.includes(
            option.value,
          ),
      );
    }, [
      managerRole,
      statusOptions,
      allowedNextStatuses,
    ]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const response =
        await ordersApi.getOrder(
          orderId,
        );

      setOrder(
        response.data?.data ||
          null,
      );
    } catch (error) {
      const message =
        getApiErrorMessage(error);

      setOrder(null);
      setLoadError(message);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const canCurrentRoleViewOrder =
    useMemo(() => {
      if (!order) {
        return true;
      }

      if (managerRole) {
        return true;
      }

      if (waiterRole) {
        return WAITER_VISIBLE_STATUSES.includes(
          orderStatus,
        );
      }

      if (kitchenRole) {
        return KITCHEN_VISIBLE_STATUSES.includes(
          orderStatus,
        );
      }

      return false;
    }, [
      order,
      managerRole,
      waiterRole,
      kitchenRole,
      orderStatus,
    ]);

  useEffect(() => {
    if (
      loading ||
      !order ||
      canCurrentRoleViewOrder
    ) {
      return;
    }

    navigate('/orders', {
      replace: true,
    });
  }, [
    loading,
    order,
    canCurrentRoleViewOrder,
    navigate,
  ]);

  const updateStatus = async (
    status,
  ) => {
    if (!order) {
      return;
    }

    const nextStatus =
      normalizeStatus(status);

    if (
      !managerRole &&
      !allowedNextStatuses.includes(
        nextStatus,
      )
    ) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t(
          'orders.details.statusNotAllowed',
        ),
      });

      return;
    }

    try {
      setUpdatingStatus(
        nextStatus,
      );

      const response =
        await ordersApi.updateOrderStatus(
          order.id,
          nextStatus,
        );

      setOrder(
        response.data?.data ||
          response.data ||
          order,
      );

      showAlert({
        type: 'success',
        title: t(
          'alerts.success',
        ),
        message: t(
          'orders.alerts.statusUpdated',
        ),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message:
          getApiErrorMessage(
            error,
          ),
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusLabel = (
    status,
  ) => {
    return t(
      `orders.status.${status}`,
    );
  };

  const getTypeLabel = (type) => {
    return t(
      `orders.types.${type}`,
    );
  };

  const formattedDate =
    order?.created_at
      ? new Date(
          order.created_at,
        ).toLocaleString(
          language || undefined,
        )
      : '-';

  if (loading) {
    return (
      <main className="order-details-page">
        <section className="order-details-state-panel">
          <div className="order-details-loading">
            <Loader2 size={30} />

            <span>
              {t(
                'orders.details.loading',
              )}
            </span>
          </div>
        </section>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="order-details-page">
        <section className="order-details-error-panel">
          <div className="order-details-error-panel__content">
            <XCircle size={36} />

            <h1>
              {t(
                'orders.messages.unableToLoadDetails',
              )}
            </h1>

            {loadError ? (
              <p>{loadError}</p>
            ) : null}
          </div>

          <Link
            to="/orders"
            className="order-details-back-button"
          >
            <ArrowLeft
              size={18}
              className="order-details-back-icon"
            />

            <span>
              {t(
                'orders.details.back',
              )}
            </span>
          </Link>
        </section>
      </main>
    );
  }

  const StatusIcon =
    statusIcons[orderStatus] ||
    Clock3;

  const hasStatusActions =
    visibleStatusOptions.length > 0;

  const changeStatusDescription =
    managerRole
      ? t(
          'orders.details.changeStatusDescription',
        )
      : t(
          'orders.details.roleLimitedStatusDescription',
        );

  return (
    <main className="order-details-page">
      <header className="order-details-header">
        <Link
          to="/orders"
          className="order-details-back-button"
        >
          <ArrowLeft
            size={18}
            className="order-details-back-icon"
          />

          <span>
            {t(
              'orders.details.back',
            )}
          </span>
        </Link>

        <div className="order-details-header__main">
          <div className="order-details-header__copy">
            <p className="order-details-eyebrow">
              {t(
                'orders.details.eyebrow',
              )}
            </p>

            <h1
              dir="ltr"
              className="order-details-order-number"
            >
              {order.order_number}
            </h1>

            <div className="order-details-header__meta">
              <span>
                <UserRound
                  size={16}
                />

                <strong>
                  {order.customer_name ||
                    '-'}
                </strong>
              </span>

              <span dir="ltr">
                <Phone size={16} />

                <strong>
                  {order.customer_phone ||
                    '-'}
                </strong>
              </span>
            </div>
          </div>

          <div className="order-details-header__icon">
            <ShoppingBag size={30} />
          </div>
        </div>
      </header>

      <section className="order-details-status-card">
        <div className="order-details-status-card__heading">
          <span
            className={`order-details-status-badge order-details-status-badge--${orderStatus}`}
          >
            <StatusIcon size={17} />

            {getStatusLabel(
              orderStatus,
            )}
          </span>

          <div>
            <h2>
              {t(
                'orders.details.changeStatus',
              )}
            </h2>

            <p>
              {
                changeStatusDescription
              }
            </p>
          </div>
        </div>

        <div className="order-details-status-actions">
          {hasStatusActions ? (
            visibleStatusOptions.map(
              (option) => {
                const OptionIcon =
                  statusIcons[
                    option.value
                  ] ||
                  CheckCircle2;

                const active =
                  orderStatus ===
                  option.value;

                const updatingThisStatus =
                  updatingStatus ===
                  option.value;

                return (
                  <button
                    key={
                      option.value
                    }
                    type="button"
                    className={`order-details-status-option order-details-status-option--${option.value} ${
                      active
                        ? 'is-active'
                        : ''
                    }`}
                    onClick={() =>
                      updateStatus(
                        option.value,
                      )
                    }
                    disabled={
                      Boolean(
                        updatingStatus,
                      ) || active
                    }
                  >
                    <span className="order-details-status-option__indicator" />

                    {updatingThisStatus ? (
                      <Loader2
                        size={16}
                        className="order-details-status-option__loader"
                      />
                    ) : (
                      <OptionIcon
                        size={16}
                        className="order-details-status-option__icon"
                      />
                    )}

                    <span>
                      {option.label}
                    </span>
                  </button>
                );
              },
            )
          ) : (
            <div className="order-details-status-empty">
              {t(
                'orders.details.noRoleActions',
              )}
            </div>
          )}
        </div>
      </section>

      <section className="order-details-summary">
        <article>
          <span className="order-details-summary__icon">
            <StatusIcon size={19} />
          </span>

          <div>
            <small>
              {t(
                'orders.table.status',
              )}
            </small>

            <strong>
              {getStatusLabel(
                orderStatus,
              )}
            </strong>
          </div>
        </article>

        <article>
          <span className="order-details-summary__icon">
            <ShoppingBag size={19} />
          </span>

          <div>
            <small>
              {t(
                'orders.table.type',
              )}
            </small>

            <strong>
              {getTypeLabel(
                order.order_type,
              )}
            </strong>
          </div>
        </article>

        <article>
          <span className="order-details-summary__icon">
            <WalletCards size={19} />
          </span>

          <div>
            <small>
              {t(
                'orders.table.total',
              )}
            </small>

            <strong dir="ltr">
              {order.total_amount}{' '}
              {order.currency}
            </strong>
          </div>
        </article>

        <article>
          <span className="order-details-summary__icon">
            <CalendarDays size={19} />
          </span>

          <div>
            <small>
              {t(
                'orders.table.date',
              )}
            </small>

            <strong>
              {formattedDate}
            </strong>
          </div>
        </article>
      </section>

      <section className="order-details-grid">
        <article className="order-details-panel order-details-panel--items">
          <div className="order-details-panel__head">
            <span>
              <ReceiptText
                size={21}
              />
            </span>

            <div>
              <h2>
                {t(
                  'orders.details.itemsTitle',
                )}
              </h2>

              <p>
                {t(
                  'orders.details.itemsDescription',
                )}
              </p>
            </div>
          </div>

          <div className="order-details-items-grid">
            {groupedOrderItems.map(
              (item) => {
                const itemImage =
                  getOrderItemImage(
                    item,
                  );

                return (
                  <article
                    key={`order-item-${item.id}-${item.product_id}-${item.size_name || ''}`}
                    className="order-details-item-card"
                  >
                    <div className="order-details-item-card__head">
                      <div className="order-details-item-card__product">
                        <span className="order-details-item-card__image">
                          {itemImage ? (
                            <img
                              src={
                                itemImage
                              }
                              alt={
                                item.product_name ||
                                t(
                                  'orders.details.product',
                                )
                              }
                            />
                          ) : (
                            <ImageIcon
                              size={24}
                            />
                          )}
                        </span>

                        <div>
                          <small>
                            {t(
                              'orders.details.product',
                            )}
                          </small>

                          <strong>
                            {item.product_name ||
                              '-'}
                          </strong>
                        </div>
                      </div>

                      <span className="order-details-item-card__total">
                        {item.total_price}{' '}
                        {order.currency}
                      </span>
                    </div>

                    <div className="order-details-item-card__meta">
                      <span>
                        <small>
                          {t(
                            'orders.details.size',
                          )}
                        </small>

                        <strong>
                          {item.size_name ||
                            '-'}
                        </strong>
                      </span>

                      <span>
                        <small>
                          {t(
                            'orders.details.price',
                          )}
                        </small>

                        <strong dir="ltr">
                          {
                            item.unit_price
                          }{' '}
                          {
                            order.currency
                          }
                        </strong>
                      </span>

                      <span>
                        <small>
                          {t(
                            'orders.details.quantity',
                          )}
                        </small>

                        <strong>
                          {item.quantity}
                        </strong>
                      </span>
                    </div>

                    {item.note ? (
                      <div className="order-details-item-card__note">
                        <small>
                          {t(
                            'orders.details.note',
                          )}
                        </small>

                        <p>
                          {item.note}
                        </p>
                      </div>
                    ) : null}
                  </article>
                );
              },
            )}

            {!groupedOrderItems.length ? (
              <div className="order-details-items-empty">
                <ReceiptText
                  size={26}
                />

                <span>
                  {t(
                    'orders.details.noItems',
                  )}
                </span>
              </div>
            ) : null}
          </div>
        </article>

        <aside className="order-details-side">
          <article className="order-details-panel">
            <div className="order-details-panel__head">
              <span>
                <UserRound
                  size={21}
                />
              </span>

              <div>
                <h2>
                  {t(
                    'orders.details.customerTitle',
                  )}
                </h2>
              </div>
            </div>

            <div className="order-details-info-list">
              <div>
                <span>
                  {t(
                    'orders.table.customer',
                  )}
                </span>

                <strong>
                  {order.customer_name ||
                    '-'}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    'orders.table.phone',
                  )}
                </span>

                <strong dir="ltr">
                  {order.customer_phone ||
                    '-'}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    'orders.table.type',
                  )}
                </span>

                <strong>
                  {getTypeLabel(
                    order.order_type,
                  )}
                </strong>
              </div>

              {order.table_number ? (
                <div>
                  <span>
                    {t(
                      'orders.table.tableNumber',
                    )}
                  </span>

                  <strong>
                    {
                      order.table_number
                    }
                  </strong>
                </div>
              ) : null}
            </div>
          </article>

          {order.customer_note ? (
            <article className="order-details-panel order-details-note">
              <div className="order-details-panel__head">
                <span>
                  <ReceiptText
                    size={21}
                  />
                </span>

                <div>
                  <h2>
                    {t(
                      'orders.details.customerNote',
                    )}
                  </h2>
                </div>
              </div>

              <p>
                {order.customer_note}
              </p>
            </article>
          ) : null}
        </aside>
      </section>
    </main>
  );
}

export default OrderDetails;